import type { SentryEntityKind } from '@depanneo/types'
import * as Sentry from '@sentry/react'

/**
 * Sentry tag key per entity kind. Typed `Record<SentryEntityKind, ...>` so it is
 * EXHAUSTIVE against the shared union in @depanneo/types — adding a kind there
 * makes this a compile error until it's added here, so web + API can't drift.
 * (Type-only import of the union keeps this module free of a runtime
 * @depanneo/types dependency.)
 */
const ENTITY_TAG_KEY: Record<SentryEntityKind, string> = {
  intervention: 'intervention_id',
  invoice: 'invoice_id',
  quote: 'quote_id',
  technician: 'technician_id',
}

/**
 * Tags subsequent Sentry events with the entity id the current view concerns
 * (`intervention_id`/`invoice_id`/`quote_id`/`technician_id`), so a browser
 * error can be filtered to its intervention/invoice/quote/technician (OBS-1).
 *
 * ids only — never row PII. #1342's scrubber is the backstop.
 */
export function setEntityTag(kind: SentryEntityKind, id: string): void {
  Sentry.setTag(ENTITY_TAG_KEY[kind], id)
}

/**
 * Clears ALL entity-id tags. The browser Sentry scope is single + long-lived,
 * so an entity tag set on one view would otherwise persist onto later, unrelated
 * events (mis-attribution) and survive logout (cross-tenant leak on an anon
 * event). Called before every navigation (router.ts) and on logout (auth store);
 * the destination route's loader re-stamps its own entity id.
 */
export function clearEntityTags(): void {
  for (const key of Object.values(ENTITY_TAG_KEY)) {
    Sentry.setTag(key, undefined)
  }
}
